import Store from 'repatch';

import State from '../core/State';
import Agent from '../entity/Agent';
import {
  storeAgentInvitation,
  storeAgentMemberships,
  storeCurrentAgent,
  storeDeleteAgentMembership,
  storeUpdateAgentMembership,
} from '../reducer/agent';
import ApiErrorHandler from '../service/ApiErrorHandler';
import MessageDispatcher from '../service/MessageDispatcher';
import AgentWebApi from '../service/persistent/AgentWebApi';

class AgentUsecase {
  private store: Store<State>;
  private apiErrorHandler: ApiErrorHandler;
  private agentWebApi: AgentWebApi;
  private messageDispatcher: MessageDispatcher;

  constructor({
    store,
    apiErrorHandler,
    agentWebApi,
    messageDispatcher,
  }: {
    store: Store<State>;
    apiErrorHandler: ApiErrorHandler;
    agentWebApi: AgentWebApi;
    messageDispatcher: MessageDispatcher;
  }) {
    this.store = store;
    this.apiErrorHandler = apiErrorHandler;
    this.agentWebApi = agentWebApi;
    this.messageDispatcher = messageDispatcher;
  }

  public currentAgent = async (): Promise<{
    id: string;
    name: string;
    comment: string;
    permissions: { action: string; permission: boolean }[];
  }> => {
    try {
      const result = await this.agentWebApi.currentAgent();
      this.store.dispatch(storeCurrentAgent(result));
      return result;
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject(err);
    }
  };

  public updateAgent = async (
    id: string,
    name: string,
    comment: string,
  ): Promise<{
    id: string;
    name: string;
    comment?: string;
    permissions?: { action: string; permission: boolean }[];
  }> => {
    try {
      const result = await this.agentWebApi.updateAgent(id, name, comment);
      const { currentAgent } = this.store.getState();
      const agent = {
        id: result.id,
        name: result.name,
        comment: result.comment ?? currentAgent?.comment,
        permissions: result.permissions ?? currentAgent?.permissions,
      };
      this.store.dispatch(storeCurrentAgent(agent));
      this.messageDispatcher.notifyUnimportant('agent_setting.agent_name.message.saved');
      return result;
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject(err);
    }
  };

  public agentMemberships = async (agent_id: string): Promise<any> => {
    try {
      const result = await this.agentWebApi.agentMemberships(agent_id);
      this.store.dispatch(storeAgentMemberships(result));
      return result;
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject(err);
    }
  };

  public createAgentMembership = async (token: string): Promise<any> => {
    try {
      const result = await this.agentWebApi.createAgentMembership(token);
      return result;
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject(err);
    }
  };

  public updateAgentMembership = async (id: string, role_names: string[]): Promise<Record<string, unknown>> => {
    try {
      const result = await this.agentWebApi.updateAgentMembership(id, role_names);
      this.store.dispatch(storeUpdateAgentMembership(result.agentMembership));
      this.messageDispatcher.notifyUnimportant('agent_setting.memberships.message.roles_updated');
      return result;
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject(err);
    }
  };

  public deleteAgentMembership = async (id: string): Promise<Record<string, unknown>> => {
    try {
      const result = await this.agentWebApi.deleteAgentMembership(id);
      this.store.dispatch(storeDeleteAgentMembership(result));
      this.messageDispatcher.notifyUnimportant('agent_setting.memberships.message.member_deleted');
      return result;
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject(err);
    }
  };

  public agentInvitation = async (
    agent_id: string,
  ): Promise<{
    agent: Agent;
    enabled: boolean;
    id: string;
    token: string;
  }> => {
    try {
      const result = await this.agentWebApi.agentInvitation(agent_id);
      this.store.dispatch(storeAgentInvitation(result));
      return result;
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject(err);
    }
  };

  public updateAgentInvitation = async (id: string, enabled: boolean): Promise<Record<string, unknown>> => {
    try {
      const result = await this.agentWebApi.updateAgentInvitation(id, enabled);
      this.messageDispatcher.notifyUnimportant('agent_setting.invitation.message.disable_url');
      return result;
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject(err);
    }
  };

  public regenerateAgentInvitation = async (
    agent_id: string,
  ): Promise<{ agent: Agent; enabled: boolean; id: string; token: string }> => {
    try {
      const result = await this.agentWebApi.regenerateAgentInvitation(agent_id);
      return result.agentInvitation;
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject(err);
    }
  };

  public agent = async (
    invitationToken: string,
  ): Promise<{
    id: string;
    name: string;
  }> => {
    try {
      const result = await this.agentWebApi.agent(invitationToken);
      return result;
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject(err);
    }
  };
}

export default AgentUsecase;
